import { useEffect, useState, type ReactElement } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import { CommonEmptyDataV1, CommonStatusDisplayV1 } from 'gemlux-common-components';
import { inviteActions } from '../../../redux/invite/inviteSlice';
import { useAppDispatch } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import DeleteSvg from '../../../assets/img/delete-red.svg';
import ResendSvg from '../../../assets/img/resend-icon.svg';
import CustomTable, { ColumnProps } from '../../V1/CustomTable/CustomTable';
import { SortOrder } from '../../../utils/enums/sort-order.enum';
import RecallInvitation from '../RecallInvitaion/RecallInvitation';
import LimitModal from '../SubordinatesTabs/LimitModal/LimitModal';
import { useAuth } from '../../../context/AuthContext';
import { UsageType } from '../../../utils/enums/usage-type.enum';
import { InviteStatus } from '../../../utils/enums/invite-status.enum';

export default function InvitationsTable(): ReactElement {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const rowsPerPageParam = searchParams.get('rows-per-page');
  const sortByParam = searchParams.get('sort-by');
  const sortOrderParam = searchParams.get('sort-order');
  const [page, setPage] = useState<number>(pageParam ? parseInt(pageParam, 10) : 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : 10);
  const [sortBy, setSortBy] = useState<string>(sortByParam ?? 'createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>((sortOrderParam as SortOrder) ?? SortOrder.DESC);

  const { invites, total, componentStatus } = useSelector((state: RootState) => state.invite);
  const invitesLoading = Boolean(_.get(componentStatus, 'invitesLoading', false));
  const recallInvitationLoading = Boolean(_.get(componentStatus, 'recallInvitationLoading', false));
  const userLimit = user.usage.find((item: { type: UsageType }) => item.type === UsageType.USER_LIMIT).limit;
  const subordinateUsage = user.usage.find((item: { type: UsageType }) => item.type === UsageType.USER_LIMIT).usage;
  const hasExceededUserLimit = user.isNccUser ? false : userLimit <= subordinateUsage;

  const [selectedInvitation, setSelectedInvitation] = useState<any>(null);
  const [isRecallModalOpen, setIsRecallModalOpen] = useState<boolean>(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);

  useEffect(() => {
    // When search params change, update local state accordingly
    if (pageParam !== null) {
      setPage(parseInt(pageParam, 10));
    }
    if (rowsPerPageParam !== null) {
      setRowsPerPage(parseInt(rowsPerPageParam, 10));
    }
    if (sortByParam !== null) {
      setSortBy(sortByParam);
    }
    if (sortOrderParam !== null) {
      setSortOrder(sortOrderParam as SortOrder);
    }
  }, [searchParams, pageParam, rowsPerPageParam, sortByParam, sortOrderParam]);

  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      'rows-per-page': rowsPerPage.toString(),
      'sort-by': sortBy,
      'sort-order': sortOrder,
    });
  }, [page, rowsPerPage, sortBy, sortOrder, setSearchParams]);

  useEffect(() => {
    if (!recallInvitationLoading)
      dispatch(inviteActions.getInvitationsAsync({ page, limit: rowsPerPage, sortBy, sortOrder }));
  }, [dispatch, page, rowsPerPage, sortBy, sortOrder, recallInvitationLoading]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (rows: number) => {
    const totalPages = Math.ceil(total / rows);
    const newPage = Math.min(page, totalPages);
    setPage(newPage);
    setRowsPerPage(rows);
  };

  const handleRecallClick = (invitation: any, isRecalling: boolean) => {
    if (hasExceededUserLimit && !isRecalling) {
      setIsLimitModalOpen(true);
      return;
    }
    setSelectedInvitation(invitation);
    setIsRecallModalOpen(true);
  };

  const handleConfirmRecall = () => {
    if (selectedInvitation && selectedInvitation.id) {
      dispatch(inviteActions.recallInvitationAsync(selectedInvitation.id));
    }
    setIsRecallModalOpen(false);
  };
  const handleManageSubscription = () => {
    navigate('/manage-subscription');
  };

  const columns: ColumnProps[] = [
    {
      key: 'systemId',
      name: 'ID',
      enableSort: true,
      align: 'left',
      width: '10%',
      resizable: false,
    },
    {
      key: 'email',
      name: 'Email Address',
      enableSort: false,
      align: 'left',
      width: '20%',
      resizable: false,
    },
    {
      key: 'createdAt',
      name: 'Invite Date',
      enableSort: true,
      align: 'left',
      width: '25%',
      resizable: false,
    },
    {
      key: 'status',
      name: 'Status',
      enableSort: true,
      align: 'left',
      width: '10%',
      resizable: false,
    },
    {
      key: 'actions',
      name: 'Actions',
      enableSort: false,
      align: 'center',
      width: '5%',
      resizable: false,
    },
  ];

  const rows = invites.map((item: any) => ({
    id: item.id,
    systemId: item.systemId.toString().padStart(5, '0'),
    email: item.email,
    createdAt: dayjs(item.createdAt).format('DD/MM/YYYY HH:mm:ss'),
    status: <CommonStatusDisplayV1 status={item.status} />,
    actions: (
      <Box display="flex" justifyContent="center">
        <Tooltip
          title={item.status === InviteStatus.PENDING ? 'Recall' : 'Resend'}
          placement="left-end"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#000',
                '& .MuiTooltip-arrow': {
                  color: '#000',
                },
                color: '#fff',
              },
            },
          }}
          arrow
        >
          <Box
            component="img"
            src={item.status === InviteStatus.PENDING ? DeleteSvg : ResendSvg}
            alt="View"
            sx={{
              width: 20,
              height: 20,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={() => handleRecallClick(item, item.status === InviteStatus.PENDING)}
          />
        </Tooltip>
      </Box>
    ),
  }));

  return (
    <>
      <Box paddingRight="2%">
        {invites.length === 0 && !invitesLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="50vh">
            <CommonEmptyDataV1
              title="No Invitations!"
              description="Once you send invites, they will be displayed here."
            />
          </Box>
        ) : (
          <CustomTable
            isLoading={invitesLoading}
            totalDataRows={total}
            enablePagination
            selectedPage={page}
            enablePagePerRows
            rowsPerList={[10, 15, 20]}
            selectedRowsPerPage={rowsPerPage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            columns={columns}
            rows={rows}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleSortByChange={setSortBy}
            handleSortOrderChange={setSortOrder}
            tableType="Invitations"
          />
        )}
        <RecallInvitation
          open={isRecallModalOpen}
          onClose={() => setIsRecallModalOpen(false)}
          onRecall={handleConfirmRecall}
          email={selectedInvitation ? selectedInvitation.email : ''}
          isRecalling={selectedInvitation ? selectedInvitation.status === InviteStatus.PENDING : false}
        />
      </Box>

      <LimitModal
        handleManageSubscription={handleManageSubscription}
        isLimitModalOpen={isLimitModalOpen}
        setIsLimitModalOpen={setIsLimitModalOpen}
      />
    </>
  );
}
